import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Upload, Delete, CheckCircle, Refresh, ViewCarousel, Image as ImageIcon, ArrowUpward, ArrowDownward, PowerSettingsNew } from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

const HeroImageManagement = () => {
  const [sectionType, setSectionType] = useState('static'); // 'static' or 'carousel'
  const [activeHeroType, setActiveHeroType] = useState('static'); // Which type is active on landing page

  // Static Hero States
  const [heroImages, setHeroImages] = useState([]);
  const [staticLoading, setStaticLoading] = useState(true);

  // Carousel States
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [carouselLoading, setCarouselLoading] = useState(true);

  // Common States
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);

  // Dialog States (for both static and carousel)
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageTitle, setImageTitle] = useState('');
  const [imageDescription, setImageDescription] = useState('');
  const [slideOrder, setSlideOrder] = useState(0);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchHeroImages();
    fetchCarouselSlides();
    fetchHeroSettings();
  }, []);

  const fetchHeroImages = async () => {
    try {
      setStaticLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/hero-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHeroImages(response.data.heroImages || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch hero images');
      console.error('Error fetching hero images:', err);
    } finally {
      setStaticLoading(false);
    }
  };

  const fetchCarouselSlides = async () => {
    try {
      setCarouselLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/carousel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarouselSlides(response.data.slides || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch carousel slides');
      console.error('Error fetching carousel slides:', err);
    } finally {
      setCarouselLoading(false);
    }
  };

  const fetchHeroSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/hero-images/settings`);
      setActiveHeroType(response.data.heroType);
    } catch (err) {
      console.error('Error fetching hero settings:', err);
    }
  };

  const handleActivateHeroType = async (newHeroType) => {
    if (newHeroType === activeHeroType) return;

    try {
      setUpdatingSettings(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/hero-images/settings`,
        { heroType: newHeroType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveHeroType(newHeroType);
      setSuccess(`${newHeroType === 'static' ? 'Static Hero' : 'Carousel'} is now active on the landing page`);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update hero type');
      console.error('Error updating hero type:', err);
    } finally {
      setUpdatingSettings(false);
    }
  };

  const handleSectionChange = (event, newSection) => {
    if (newSection !== null) {
      setSectionType(newSection);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setImageTitle('');
    setImageDescription('');
    setSelectedFile(null);
    setPreviewUrl('');
    setSlideOrder(carouselSlides.length);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setImageTitle('');
    setImageDescription('');
    setSelectedFile(null);
    setPreviewUrl('');
    setSlideOrder(0);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (20MB = 20 * 1024 * 1024 bytes)
      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(`File size (${(file.size / 1024 / 1024).toFixed(2)} MB) exceeds the maximum limit of 20MB. Please choose a smaller image.`);
        setSelectedFile(null);
        setPreviewUrl('');
        return;
      }

      setSelectedFile(file);
      setError(''); // Clear any previous errors
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadStatic = async () => {
    if (!selectedFile || !imageTitle) {
      setError('Please provide both an image and a title');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('title', imageTitle);

      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/hero-images/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Hero image uploaded successfully');
      setError('');
      handleCloseDialog();
      fetchHeroImages();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload hero image');
      console.error('Error uploading hero image:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadCarousel = async () => {
    if (!selectedFile || !imageTitle) {
      setError('Please provide both an image and a title');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('title', imageTitle);
      formData.append('description', imageDescription);
      formData.append('order', slideOrder);

      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/carousel/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Carousel slide uploaded successfully');
      setError('');
      handleCloseDialog();
      fetchCarouselSlides();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload carousel slide');
      console.error('Error uploading carousel slide:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSetActive = async (imageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/hero-images/${imageId}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Hero image set as active');
      setError('');
      fetchHeroImages();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set active hero image');
      console.error('Error setting active hero image:', err);
    }
  };

  const handleDeleteStatic = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this hero image?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/hero-images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Hero image deleted successfully');
      setError('');
      fetchHeroImages();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete hero image');
      console.error('Error deleting hero image:', err);
    }
  };

  const handleDeleteCarousel = async (slideId) => {
    if (!window.confirm('Are you sure you want to delete this carousel slide?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/carousel/${slideId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Carousel slide deleted successfully');
      setError('');
      fetchCarouselSlides();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete carousel slide');
      console.error('Error deleting carousel slide:', err);
    }
  };

  const handleMoveSlide = async (slideId, direction) => {
    const slideIndex = carouselSlides.findIndex(s => s.id === slideId);
    if (
      (direction === 'up' && slideIndex === 0) ||
      (direction === 'down' && slideIndex === carouselSlides.length - 1)
    ) {
      return;
    }

    const newSlides = [...carouselSlides];
    const targetIndex = direction === 'up' ? slideIndex - 1 : slideIndex + 1;
    [newSlides[slideIndex], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[slideIndex]];

    // Update orders
    const slideOrders = newSlides.map((slide, index) => ({
      id: slide.id,
      order: index
    }));

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/carousel/reorder`,
        { slideOrders },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCarouselSlides();
    } catch (err) {
      setError('Failed to reorder slides');
      console.error('Error reordering slides:', err);
    }
  };

  const loading = sectionType === 'static' ? staticLoading : carouselLoading;

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        Hero Section Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
        Manage hero images and carousel slides for the landing page at https://lmtss.net/
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Active Hero Type Control */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.1rem', sm: '1.25rem' }, flexWrap: 'wrap' }}>
          <PowerSettingsNew sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
          Active Hero Type on Landing Page
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
          Toggle which hero section is displayed on https://lmtss.net/
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, sm: 2, md: 3 }, flexWrap: 'wrap' }}>
          <Box
            onClick={() => !updatingSettings && handleActivateHeroType('static')}
            sx={{
              cursor: updatingSettings ? 'not-allowed' : 'pointer',
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              backgroundColor: activeHeroType === 'static' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
              border: '2px solid',
              borderColor: activeHeroType === 'static' ? 'white' : 'transparent',
              transition: 'all 0.3s',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: { xs: 140, sm: 160, md: 180 }
            }}
          >
            <ImageIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Static Hero
              </Typography>
              {activeHeroType === 'static' && (
                <Chip label="ACTIVE" size="small" color="success" sx={{ mt: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
              )}
            </Box>
          </Box>
          <Box
            onClick={() => !updatingSettings && handleActivateHeroType('carousel')}
            sx={{
              cursor: updatingSettings ? 'not-allowed' : 'pointer',
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              backgroundColor: activeHeroType === 'carousel' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
              border: '2px solid',
              borderColor: activeHeroType === 'carousel' ? 'white' : 'transparent',
              transition: 'all 0.3s',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: { xs: 140, sm: 160, md: 180 }
            }}
          >
            <ViewCarousel sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Carousel
              </Typography>
              {activeHeroType === 'carousel' && (
                <Chip label="ACTIVE" size="small" color="success" sx={{ mt: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
              )}
            </Box>
          </Box>
        </Box>
        {updatingSettings && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} sx={{ color: 'white' }} />
          </Box>
        )}
      </Paper>

      {/* Section Type Toggle */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={sectionType}
          exclusive
          onChange={handleSectionChange}
          aria-label="hero section type"
          sx={{ boxShadow: 2 }}
        >
          <ToggleButton value="static" aria-label="static hero">
            <ImageIcon sx={{ mr: 1 }} />
            Static Hero
          </ToggleButton>
          <ToggleButton value="carousel" aria-label="carousel hero">
            <ViewCarousel sx={{ mr: 1 }} />
            Carousel
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Upload sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
          onClick={handleOpenDialog}
          
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          {sectionType === 'static' ? 'Upload New Hero Image' : 'Add New Carousel Slide'}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Refresh sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
          onClick={sectionType === 'static' ? fetchHeroImages : fetchCarouselSlides}
          
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          Refresh
        </Button>
      </Box>

      {/* Static Hero Section */}
      {sectionType === 'static' && (
        <>
          {heroImages.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No hero images uploaded yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Upload your first hero image to get started
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {heroImages.map((image) => (
                <Grid item xs={12} sm={6} md={4} key={image.id}>
                  <Card sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {image.isActive && (
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                        sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
                      />
                    )}
                    <CardMedia
                      component="img"
                      height="200"
                      image={`${API_BASE_URL}${image.imageUrl}`}
                      alt={image.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {image.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Uploaded: {new Date(image.createdAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      {!image.isActive && (
                        <Button
                          size="small"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => handleSetActive(image.id)}
                        >
                          Set Active
                        </Button>
                      )}
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteStatic(image.id)}
                        title="Delete Image"
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Carousel Section */}
      {sectionType === 'carousel' && (
        <>
          {carouselSlides.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No carousel slides created yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add your first carousel slide to get started
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {carouselSlides.map((slide, index) => (
                <Grid item xs={12} sm={6} md={4} key={slide.id}>
                  <Card sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Chip
                      label={`Order: ${index + 1}`}
                      color="primary"
                      size="small"
                      sx={{ position: 'absolute', top: 10, left: 10, zIndex: 1 }}
                    />
                    {slide.isActive && (
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                        sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
                      />
                    )}
                    <CardMedia
                      component="img"
                      height="200"
                      image={`${API_BASE_URL}${slide.imageUrl}`}
                      alt={slide.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {slide.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {slide.description || 'No description'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Added: {new Date(slide.createdAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between' }}>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveSlide(slide.id, 'up')}
                          disabled={index === 0}
                          title="Move Up"
                        >
                          <ArrowUpward />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveSlide(slide.id, 'down')}
                          disabled={index === carouselSlides.length - 1}
                          title="Move Down"
                        >
                          <ArrowDownward />
                        </IconButton>
                      </Box>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteCarousel(slide.id)}
                        title="Delete Slide"
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Upload Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={window.innerWidth < 600}>
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, p: { xs: 2, sm: 3 } }}>
          {sectionType === 'static' ? 'Upload New Hero Image' : 'Add New Carousel Slide'}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              value={imageTitle}
              onChange={(e) => setImageTitle(e.target.value)}
              fullWidth
              required
              placeholder={sectionType === 'static' ? 'e.g., Summer Hero Image' : 'e.g., Welcome to LMTSS'}
            />
            {sectionType === 'carousel' && (
              <>
                <TextField
                  label="Description"
                  value={imageDescription}
                  onChange={(e) => setImageDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Brief description of this slide"
                />
                <TextField
                  label="Order"
                  type="number"
                  value={slideOrder}
                  onChange={(e) => setSlideOrder(parseInt(e.target.value))}
                  fullWidth
                  helperText="Slides will be displayed in order (0, 1, 2, ...)"
                />
              </>
            )}
            <Button
              variant="outlined"
              component="label"
              fullWidth
            >
              Select Image File
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Maximum file size: 20MB. Supported formats: JPG, PNG, GIF, WebP
            </Typography>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
            {previewUrl && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Preview:
                </Typography>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '8px' }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={sectionType === 'static' ? handleUploadStatic : handleUploadCarousel}
            variant="contained"
            color="primary"
            disabled={uploading || !selectedFile || !imageTitle}
            startIcon={uploading ? <CircularProgress size={20} /> : <Upload />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HeroImageManagement;
